"use strict";
// Copyright © Veeam Software Group GmbH
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRequest = void 0;
const react_1 = require("react");
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const contexts_1 = require("../contexts");
const HttpProxy_1 = require("./HttpProxy");
require("./types");
const EMPTY_PARAMS = {};
const EMPTY_FILTER_QUERY = {};
const INITIAL_REQUEST_RESULT = {
    data: null,
    meta: {
        done: false,
        loading: false,
    },
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useRequest = ({ url, method, isFileUpload, responseType, skipAuthorization, httpProxy, payload, autoLoad, wait, mapper, params, filterQuery, onCatchError, onDone, onError, lockInternalOnCatchError, }) => {
    let isActive = !!autoLoad;
    if (autoLoad && wait && wait.length) {
        isActive = wait.every((i) => i.meta.done);
    }
    const { getErrorListener, getApiRequest } = contexts_1.useAppContext();
    const proxy = httpProxy ||
        new HttpProxy_1.HttpProxy({
            url,
            method,
            isFileUpload,
            responseType,
            skipAuthorization,
        });
    const proxyRef = react_1.useRef(proxy);
    const unsubscribeRef = react_1.useRef(null);
    const paramsRef = react_1.useRef(null);
    const payloadRef = react_1.useRef(null);
    const filterQueryRef = react_1.useRef(null);
    const [loading, setLoading] = react_1.useState(false);
    const [done, setDone] = react_1.useState(false);
    const [error, setError] = react_1.useState('');
    const internalOnCatchError = onCatchError || getErrorListener().onCatchError;
    if (typeof proxyRef.current.setContext === 'function') {
        proxyRef.current.setContext(getApiRequest());
    }
    const setParams = (newParams = EMPTY_PARAMS) => {
        if (!fast_deep_equal_1.default(paramsRef.current, newParams)) {
            paramsRef.current = newParams;
        }
    };
    setParams(params || EMPTY_PARAMS);
    const setPayload = (newPayload) => {
        if (!fast_deep_equal_1.default(payloadRef.current, newPayload)) {
            payloadRef.current = newPayload;
        }
    };
    setPayload(payload || null);
    const setFilterQuery = (newFilterQuery = EMPTY_FILTER_QUERY) => {
        if (!fast_deep_equal_1.default(filterQueryRef.current, newFilterQuery)) {
            filterQueryRef.current = newFilterQuery;
        }
    };
    setFilterQuery(filterQuery || EMPTY_FILTER_QUERY);
    const requestResultRef = react_1.useRef(null);
    const [result, setResult] = react_1.useState(INITIAL_REQUEST_RESULT);
    const load = (config = {}) => {
        setLoading(true);
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }
        return new Promise((resolve) => {
            const subscription = proxyRef.current
                .load(config.payload || payloadRef.current || null, config.params || paramsRef.current, config.filterQuery || filterQueryRef.current)
                .subscribe((value) => {
                const newData = value.data;
                let prevResult = result;
                requestResultRef.current = mapper ? mapper(newData) : newData;
                const newResult = {
                    data: requestResultRef.current,
                    meta: {
                        loading: false,
                        done: true,
                    },
                };
                setError('');
                setResult((prev) => {
                    prevResult = prev;
                    return newResult;
                });
                setLoading(false);
                setDone(true);
                resolve(newResult);
                if (onDone) {
                    onDone(newResult, prevResult);
                }
            }, (err) => {
                const errorMessage = err.displayMessage || err.message;
                const newResult = {
                    data: requestResultRef.current,
                    meta: {
                        loading: false,
                        done: true,
                        error: errorMessage,
                    },
                };
                setError(errorMessage);
                setResult(newResult);
                setLoading(false);
                resolve(newResult);
                if (!lockInternalOnCatchError) {
                    internalOnCatchError(err);
                }
                onError === null || onError === void 0 ? void 0 : onError(err);
            });
            unsubscribeRef.current = () => {
                subscription.unsubscribe();
            };
        });
    };
    react_1.useEffect(() => () => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }
    }, []);
    react_1.useEffect(() => {
        if (isActive) {
            load({ payload });
        }
    }, [isActive, paramsRef.current]);
    const finalResult = {
        data: result.data,
        meta: {
            loading,
            done,
        },
    };
    if (error) {
        finalResult.meta.error = error;
    }
    const terminate = () => {
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
    };
    return [
        finalResult,
        {
            load,
            getResultData: () => requestResultRef.current,
            terminate,
        },
    ];
};
exports.useRequest = useRequest;
//# sourceMappingURL=useRequest.js.map